// core/stores/messageStore.ts — Zustand store for conversations + messages.
//
// Hydrated at module init from the typed seed (deep-clone so test resets
// don't mutate the imported seed constants).
// Services mutate this store; hooks read it reactively.
// Components must use hooks, not this store directly.

import { create } from 'zustand';
import type { Conversation, Message } from '@/core/models/message';
import { seedConversations } from '@/core/data/messages';
import { bootSeed } from './bootSeed';

interface MessageState {
  conversations: Conversation[];

  /** Replace all conversations (used by _resetForTest). */
  setConversations: (conversations: Conversation[]) => void;

  /**
   * Append a message to a conversation, deduped by id. No-op if convoId not
   * found. The dedupe matters for the realtime path: a message the user just
   * sent via messagesService.send is already in the store from the awaited
   * response, and the server also echoes it back over the socket as
   * `dm.message` — without the id check that would double-render it.
   */
  appendMessage: (convoId: string, message: Message) => void;

  /** Set a conversation's unread count to 0. No-op if convoId not found. */
  markConversationRead: (convoId: string) => void;

  /** Bump a conversation's unread count by 1. No-op if convoId not found. */
  incrementUnread: (convoId: string) => void;

  /**
   * Drop a conversation entirely. Declining a message request deletes the
   * thread for BOTH sides server-side, so the local row must go too — leaving
   * it would show a thread that no longer exists until the next poll.
   * No-op if convoId not found.
   */
  removeConversation: (convoId: string) => void;

  /**
   * Clear both request flags on a conversation (1.24.00): the recipient has
   * accepted, so the SENDER's "Request sent — they'll see it in Requests"
   * banner must go and the thread becomes an ordinary one. Driven by the
   * realtime `conversation.accepted` frame — the 10s poll that would otherwise
   * correct this is skipped entirely while the socket is up, so without this
   * the banner sits under a live thread for the rest of the session.
   * No-op if convoId not found.
   */
  markConversationAccepted: (convoId: string) => void;

  /**
   * Record the other participant's read instant (1.24.00 read receipts), from
   * the realtime `dm.read` frame. No-op if convoId not found. Deliberately
   * monotonic-by-assignment: the server only ever sends a later instant, and
   * the REST payload re-establishes truth on the next fetch.
   */
  setOtherReadAt: (convoId: string, readAt: string) => void;

  /**
   * Insert or field-merge a single conversation (by id). Used after
   * create/invite/accept, where the API hands back one fresh conversation
   * payload rather than the whole list. A new conversation is prepended
   * (most-recent-first, matching how a just-created/just-accepted thread
   * should surface). An EXISTING conversation is merged, not replaced
   * wholesale — a payload with an absent/undefined `messages` (e.g. a
   * lighter-weight response than the full serializer) must not blank an
   * open thread's already-loaded history.
   */
  upsertConversation: (conversation: Conversation) => void;
}

/** Deep-clone a single conversation (including its messages array). */
function cloneConversation(c: Conversation): Conversation {
  return {
    ...c,
    messages: c.messages.map((m) => ({ ...m })),
  };
}

export const useMessageStore = create<MessageState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate imported constants.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  conversations: bootSeed(seedConversations).map(cloneConversation),

  setConversations: (conversations) => set({ conversations }),

  appendMessage: (convoId, message) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === convoId && !c.messages.some((m) => m.id === message.id)
          ? { ...c, messages: [...c.messages, message] }
          : c,
      ),
    })),

  markConversationRead: (convoId) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === convoId ? { ...c, unread: 0 } : c,
      ),
    })),

  incrementUnread: (convoId) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === convoId ? { ...c, unread: c.unread + 1 } : c,
      ),
    })),

  removeConversation: (convoId) =>
    set((s) => ({ conversations: s.conversations.filter((c) => c.id !== convoId) })),

  markConversationAccepted: (convoId) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === convoId ? { ...c, awaitingAcceptance: false, pending: false } : c,
      ),
    })),

  setOtherReadAt: (convoId, readAt) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === convoId ? { ...c, otherReadAt: readAt } : c,
      ),
    })),

  upsertConversation: (conversation) =>
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === conversation.id);
      if (idx === -1) {
        return { conversations: [cloneConversation(conversation), ...s.conversations] };
      }
      const prev = s.conversations[idx]!;
      const merged = cloneConversation({
        ...prev,
        ...conversation,
        messages: conversation.messages ?? prev.messages,
      });
      const next = [...s.conversations];
      next[idx] = merged;
      return { conversations: next };
    }),
}));
